// ============================================================================
// Auth-retry fetch wrapper
// ============================================================================
// Worker endpoints verify the caller's Supabase JWT via GET /auth/v1/user. When
// the access token the frontend sends is expired or stale — common after a tab
// sits idle, the machine sleeps, or refresh-token rotation loses a race against
// another session/device — that verify returns 403 and the worker responds
// HTTP 401 ("Auth verify failed: 403").
//
// `supabase.auth.getSession()` (used by ~all worker call sites) returns the
// CACHED session, so it can hand back an already-expired token. Rather than fix
// 25+ call sites, this single global interceptor catches a 401 from a worker
// request, refreshes the session ONCE, and retries with the fresh token — the
// same pattern CampaignNew/BriefHub already do by hand.
//
// Scope is deliberately tight:
//   • Only worker requests (never Supabase/third-party calls).
//   • Only HTTP 401 (token invalid/expired). 403 = genuine permission denial
//     (e.g. no brand access) and is passed straight through — never retried.
//   • Only requests that actually carried an Authorization header.
//   • At most ONE retry (the retry uses the captured inner fetch, not the
//     patched window.fetch, so it can't recurse).
// Concurrent 401s share a single in-flight refresh so we don't hammer GoTrue
// (which would trip refresh-token reuse detection and kill the session).
// ============================================================================

import { supabase } from './supabase'
import { WORKER_URL } from './env'


function resolveUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.toString()
  return (input as Request).url
}

function isWorkerRequest(url: string): boolean {
  if (WORKER_URL && url.startsWith(WORKER_URL)) return true
  if (url.startsWith('/api/') || url.startsWith('/api?')) return true
  return false
}

function headersOf(input: RequestInfo | URL, init?: RequestInit): Headers {
  return new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined))
}

let installed = false
let refreshing: Promise<string | null> | null = null

// Coalesce concurrent refreshes into one GoTrue call.
function refreshAccessToken(): Promise<string | null> {
  if (!refreshing) {
    refreshing = supabase.auth
      .refreshSession()
      .then(({ data }) => data.session?.access_token ?? null)
      .catch(() => null)
      .finally(() => { refreshing = null })
  }
  return refreshing
}

export function installAuthRetryFetch(): void {
  if (installed || typeof window === 'undefined' || !window.fetch) return
  installed = true

  const innerFetch = window.fetch.bind(window)

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const res = await innerFetch(input as RequestInfo, init)

    if (res.status !== 401 || !isWorkerRequest(resolveUrl(input))) return res
    // Nothing to refresh if the request never authenticated in the first place.
    if (!headersOf(input, init).get('Authorization')) return res

    const fresh = await refreshAccessToken()
    if (!fresh) return res // refresh failed → session is dead; surface the 401

    // Reissue once with the refreshed bearer token. Use innerFetch (not the
    // patched window.fetch) so this can't loop.
    const headers = headersOf(input, init)
    headers.set('Authorization', `Bearer ${fresh}`)
    if (input instanceof Request && !init) {
      return innerFetch(new Request(input, { headers }))
    }
    return innerFetch(input as RequestInfo, { ...init, headers })
  }
}
